// MVA-kodemenyen. VatCodeSelect er den generelle velgeren: den viser grupper av
// koder med forklaring og virkning i MVA-meldingen, og eier ingen regnskapsregler
// selv. Hvilke koder som gjelder for en linje kommer fra PlummeVatCodeCatalog
// eller PlummePurchaseVatTreatment. PurchaseVatTreatmentSelect nederst er
// leverandoerbilag-varianten som jobber paa {vatRate, reverseChargeCode}.
const vatCodeText = (option, key, fallback, plain) => (
  plain || (option?.[key] ? t(option[key], option[fallback]) : '')
);
const vatCodeLabel = (option) => vatCodeText(option, 'labelKey', 'fallback', option?.label);
const vatCodeHelp = (option) => vatCodeText(option, 'contextKey', 'contextFallback') + ' ' + vatCodeText(option, 'effectKey', 'effectFallback');

function VatCodeSelect({ groups = [], value = '', onChange, disabled = false }) {
  const allOptions = groups.flatMap((group) => group.options.map((option) => ({ ...option, group })));
  const selected = allOptions.find((option) => option.value === value) || null;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [previewValue, setPreviewValue] = useState(value);
  const [menuStyle, setMenuStyle] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const preview = allOptions.find((option) => option.value === previewValue) || selected || allOptions[0];
  const normalizedQuery = query.trim().toLocaleLowerCase(PlummeI18n.intlLocale());
  const visibleGroups = groups.map((group) => ({
    ...group,
    options: group.options.filter((option) => !normalizedQuery || [
      t(group.labelKey, group.fallback),
      vatCodeLabel(option),
      vatCodeText(option, 'contextKey', 'contextFallback'),
      option.code || '',
    ].join(' ').toLocaleLowerCase(PlummeI18n.intlLocale()).includes(normalizedQuery)),
  })).filter((group) => group.options.length);

  const positionMenu = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = window.innerWidth <= 610 ? 8 : 12;
    const gap = 6;
    const width = Math.min(900, window.innerWidth - margin * 2);
    const desiredHeight = Math.min(540, window.innerHeight - margin * 2);
    const below = Math.max(0, window.innerHeight - rect.bottom - gap - margin);
    const above = Math.max(0, rect.top - gap - margin);
    const openUp = below < desiredHeight && above > below;
    const height = Math.max(220, Math.min(desiredHeight, openUp ? above : below));
    const preferredLeft = rect.left + rect.width / 2 - width / 2;
    const left = Math.max(margin, Math.min(preferredLeft, window.innerWidth - width - margin));
    setMenuStyle({
      position: 'fixed',
      top: openUp ? Math.max(margin, rect.top - gap - height) : rect.bottom + gap,
      left,
      width,
      height,
      zIndex: 10000,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setPreviewValue(value);
    positionMenu();
    const closeOutside = (event) => {
      if (wrapRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', positionMenu, true);
    };
  }, [open, positionMenu, value]);

  const pick = (option, ok) => {
    setPreviewValue(option.value);
    if (!option.selectable) return;
    if (!ok && window.innerWidth <= 610) return;
    onChange?.(option.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const detail = preview && <div className="pvt-detail">
    <span className={'pvt-scope' + (preview.kind === 'customs' ? ' customs' : '')}>{t(preview.scopeKey, preview.scopeFallback)}</span>
    <h3>{vatCodeLabel(preview)}</h3>
    <p>{vatCodeText(preview, 'contextKey', 'contextFallback')}</p>
    {preview.kind === 'customs' && <div className="pvt-customs-callout"><Icon name="info" size={17}/><span>{t('buy.vatChoice.customsProcess', 'Før leverandørfakturaen uten innførsels-MVA. Registrer deretter grunnlaget og innførsels-MVA fra tolldeklarasjonen under Innleveringer → MVA.')}</span></div>}
    <div className="pvt-effect">
      <b>{t('buy.vatChoice.resultTitle', 'Dette skjer i MVA-meldingen')}</b>
      <p>{vatCodeText(preview, 'effectKey', 'effectFallback')}</p>
    </div>
    {window.innerWidth <= 610 && preview.selectable && <Button onClick={() => pick(preview, true)}>{t('buy.vatChoice.useCode')}</Button>}
  </div>;

  const menu = open && !disabled && <div className="pvt-menu" style={menuStyle || undefined} ref={menuRef}>
    <div className="pvt-search"><Input icon="search" autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('buy.vatChoice.search', 'Søk etter MVA-kode eller bruk')} /></div>
    <div className="pvt-body">
      <div className="pvt-list" role="listbox">
        {visibleGroups.map((group) => <div key={group.key} className="pvt-group">
          <div className="pvt-group-label">{t(group.labelKey, group.fallback)}</div>
          {group.options.map((option) => <button
            type="button"
            key={option.value}
            className={'pvt-option' + (option.value === value ? ' selected' : '') + (option.value === preview?.value ? ' previewed' : '') + (!option.selectable ? ' informational' : '')}
            role="option"
            data-help-title={vatCodeLabel(option)}
            data-help={vatCodeHelp(option)}
            aria-selected={option.value === value}
            aria-disabled={!option.selectable}
            onMouseEnter={() => setPreviewValue(option.value)}
            onFocus={() => setPreviewValue(option.value)}
            onClick={() => pick(option)}
          ><span>{vatCodeLabel(option)}</span>{option.value === value && <Icon name="check" size={15}/>} {!option.selectable && <small className="pvt-info-badge">{t('buy.vatChoice.infoBadge', 'Info')}</small>}</button>)}
        </div>)}
        {!visibleGroups.length && <div className="pvt-empty">{t('buy.vatChoice.noResults', 'Ingen MVA-koder matcher søket.')}</div>}
      </div>
      {detail}
    </div>
  </div>;

  return <div className="pvt-select" ref={wrapRef}>
    <button ref={triggerRef} type="button" className="input pvt-trigger" data-help-title={selected ? vatCodeLabel(selected) : t('helpTopics.vatTreatment.title')} data-help={selected ? vatCodeHelp(selected) : t('helpTopics.vatTreatment.body')} disabled={disabled} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
      <span>{selected ? vatCodeLabel(selected) : ''}</span><Icon name={open ? 'chevUp' : 'chevDown'} size={16}/>
    </button>
    {menu && ReactDOM?.createPortal ? ReactDOM.createPortal(menu, document.body) : menu}
  </div>;
}

// A business without VAT registration has no right to deduct input VAT, so a
// purchase line only has the no-VAT choice and new lines start at 0 %.
const purchaseVatRegistered = (date) => window.PlummeVatRegistration.allowsVatOn(date);
const purchaseNewLineVatRate = (date) => (purchaseVatRegistered(date) ? 25 : 0);

// Keeps the VAT of a document's lines in step with its date. Moving the date
// before the VAT registration removes VAT from the lines; moving it back
// restores what each line had. The server still rejects VAT outside the
// registration period; this only keeps the user from getting there.
function useVatRegistrationLineSync({ date, setLines, fields, hasVat, withoutVat, enabled = true }) {
  const registered = window.PlummeVatRegistration.allowsVatOn(date);
  const previous = useRef(registered);
  const saved = useRef(new Map());
  useEffect(() => {
    if (previous.current === registered) return;
    previous.current = registered;
    if (!enabled) return;
    setLines((lines) => lines.map((line) => {
      if (!registered) {
        if (!hasVat(line)) return line;
        saved.current.set(line.id, Object.fromEntries(fields.map((field) => [field, line[field]])));
        return { ...line, ...withoutVat(line) };
      }
      const snapshot = saved.current.get(line.id);
      saved.current.delete(line.id);
      return snapshot && !hasVat(line) ? { ...line, ...snapshot } : line;
    }));
  }, [registered]);
  return registered;
}

function PurchaseVatTreatmentSelect({ vatRate = 0, reverseChargeCode = '', purchaseVatCode = '', vatTreatment = '', onChange, rates = [25, 15, 12, 0], includeReverse = true, includeImportCodes = false, disabled = false, date = '' }) {
  const settings = window.PlummeBackend?.appState?.settings || {};
  const country = String(settings.country || 'NO').toUpperCase();
  const allowCustomsInfo = country === 'NO' && purchaseVatRegistered(date);
  const allowReverse = String(reverseChargeCode || '') === '86' || (includeReverse && allowCustomsInfo);
  const options = { rates: purchaseVatRegistered(date) ? rates : [0], includeReverse: allowReverse, includeSpecial: includeImportCodes && allowCustomsInfo };
  const value = window.PlummePurchaseVatTreatment.valueFor({ vatRate, reverseChargeCode, purchaseVatCode, vatTreatment });
  const groups = window.PlummePurchaseVatTreatment.groups(options);
  const allGroups = allowCustomsInfo ? [...groups, ...window.PlummePurchaseVatTreatment.customsGroups()] : groups;
  return <VatCodeSelect
    groups={allGroups}
    value={value}
    disabled={disabled}
    onChange={(next) => onChange?.(window.PlummePurchaseVatTreatment.resolve(next))}
  />;
}

Object.assign(window, { PurchaseVatTreatmentSelect, VatCodeSelect, purchaseNewLineVatRate, useVatRegistrationLineSync });
